import { has } from 'ramda'

import { cannot } from '@/app/helpers'
import { Part, PartTypes } from '@/app/module/campaigns/types'
import type { Action } from '@/app/module/campaigns/types/schemas/actions/all-actions'

import type { ActionContainer } from './actions/types'

const concatAll = <T>(arr: Array<T[] | undefined>): T[] => arr.flatMap((a) => a || [])

export const getActions = (part: Part): Action[] => {
  switch (true) {
    case has(PartTypes.ActionsOnly, part):
      return part.actionsOnly.actions
    case has(PartTypes.QuotaActions, part):
      return concatAll([part.quotaActions.onOpen, part.quotaActions.onExhausted])
    case has(PartTypes.ApiCall, part):
      return concatAll([part.apiCall.onSuccess, part.apiCall.onError])
    case has(PartTypes.CATIMessage, part):
      return []
    case has(PartTypes.CATIQuestion, part): {
      const msg = part.catiQuestion

      return concatAll([nestedActions(msg.onAnswer), msg.openEnded?.actions])
    }
    case has(PartTypes.SMSMessage, part):
      return []
    case has(PartTypes.SMSQuestion, part): {
      const msg = part.smsQuestion

      return concatAll([
        nestedActions(msg.onAnswer),
        msg.onInvalidReply?.actions,
        msg.onRetriesExhausted?.actions,
        msg.onTimeout?.actions,
        msg.openEnded?.actions,
        nestedActions(msg.openEnded?.categories),
      ])
    }
    case has(PartTypes.Topup, part):
      return concatAll([part.topup.onSuccess, part.topup.onError])
    case has(PartTypes.VoiceMessage, part):
      return nestedActions(part.callResultActions)
    case has(PartTypes.VoiceQuestion, part): {
      const msg = part.voiceQuestion

      return concatAll([
        nestedActions(part.callResultActions),
        nestedActions(msg.onAnswer),
        msg.onInvalidReply?.actions,
        msg.onRetriesExhausted?.actions,
        msg.onTimeout?.actions,
        msg.spoken?.actions,
      ])
    }
    case has(PartTypes.WhatsAppMessage, part):
      return []
    case has(PartTypes.WhatsAppQuestion, part): {
      const msg = part.whatsappQuestion

      return concatAll([
        nestedActions(msg.onAnswer),
        nestedActions(msg.onButtons?.buttons),
        nestedActions(msg.onList?.items),
        msg.onInvalidReply?.actions,
        msg.onRetriesExhausted?.actions,
        msg.onTimeout?.actions,
        msg.openEnded?.actions,
        nestedActions(msg.openEnded?.categories),
      ])
    }
    case has(PartTypes.WhatsAppFlow, part): {
      const msg = part.whatsappFlow

      return concatAll([
        msg.onAnswer.actions,
        msg.onInvalidAnswer.actions,
        msg.onRetriesExhausted.actions,
        msg.onTimeout.actions,
      ])
    }
    default:
      return cannot(part)
  }
}

export const nestedActions = (l?: ActionContainer[] | null): Action[] => {
  if (!l) {
    return []
  }
  return l.flatMap((a) => a.actions || [])
}
